import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { useData } from '@hooks/useData';
import { useGetServicesCartTotals } from '@services/useGetServicesCartTotals';
import { useServicesCart } from '@hooks/useServicesCart';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { InputErrors } from '@types';
import { useGetAllPaymentMethods } from '@services/useGetAllPaymentMethods';
import { formatPtPhoneNumberInput, removeWhiteSpaces } from '@utils/format';
import { PaymentMethodCard } from '@components/molecules/PaymentMethodCard';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';

interface ServicesBookingPaymentErrors {
    phoneNumber: InputErrors;
}

export const ServicesBookingPayment: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { request: requestServicesCartTotals } = useGetServicesCartTotals();
    const { request: requestAllPaymentMethods } = useGetAllPaymentMethods();
    const { login, allPaymentMethods } = useData();
    const { storeUuid, services, displayWorkerStep, paymentMethod, paymentPhoneNumber, setPaymentMethod, setPaymentPhoneNumber } =
        useServicesCart();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('services-booking-payment-component.title'),
        multibancoDescription: t('services-booking-payment-component.multibanco.description'),
        mbwayDescription: t('services-booking-payment-component.mbway.description'),
        mbwayPhoneNumber: t('services-booking-payment-component.mbway.phone-number.label'),
        nextButton: t('services-booking-payment-component.next.button'),
        previousButton: t('services-booking-payment-component.previous.button'),
    };

    const CURRENT_STEP = displayWorkerStep ? 4 : 3;

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // request cart totals, when entering page
    useEffect(() => {
        if (storeUuid) {
            requestServicesCartTotals(
                { uuid: storeUuid || '' },
                {
                    services: services
                        ?.filter((service) => service.checked === true && service.quantity > 0)
                        .map((service) => {
                            return { uuid: service.uuid, quantity: service.quantity };
                        }),
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeUuid]);

    useEffect(() => {
        requestAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
    const [mbwayPhoneNumber, setMbwayPhoneNumber] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<ServicesBookingPaymentErrors>({
        phoneNumber: [],
    });

    const multibancoMethod = allPaymentMethods?.data?.find((item) => item.method === 'multibanco');
    const mbwayMethod = allPaymentMethods?.data?.find((item) => item.method === 'mbway');
    const cardMethod = allPaymentMethods?.data?.find((item) => item.method === 'card');

    // check if user already previously selected a payment method
    useEffect(() => {
        if (paymentMethod === 'mbway' && mbwayMethod) {
            setSelectedMethod(mbwayMethod.uuid || '');
            paymentPhoneNumber && setMbwayPhoneNumber(formatPtPhoneNumberInput(paymentPhoneNumber));
        } else if (paymentMethod === 'multibanco' && multibancoMethod) {
            setSelectedMethod(multibancoMethod.uuid || '');
        } else if (paymentMethod === 'card' && cardMethod) {
            setSelectedMethod(cardMethod.uuid || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentMethod]);

    const handleSavePaymentMethod = () => {
        setPaymentMethod(selectedMethod || '');
        setPaymentPhoneNumber(selectedMethod === mbwayMethod?.uuid ? removeWhiteSpaces(mbwayPhoneNumber) : null);

        // #TODO: validate phone number

        navigate(`/${SITE_URLS.servicesBookingConfirm}`);
    };

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        label={translations.nextButton}
                        mode={ButtonMode.primary}
                        onClick={handleSavePaymentMethod}
                        disabled={
                            // no method was selected
                            selectedMethod === null ||
                            // mbway is selected but no number given
                            (selectedMethod === mbwayMethod?.uuid && mbwayPhoneNumber.trim() === '')
                        }
                    />

                    <Anchor href={SITE_URLS.servicesBookingScheduling} mode={AnchorMode.primary} outline={true}>
                        {translations.previousButton}
                    </Anchor>
                </div>
            }>
            <>
                {/* multibanco */}
                {multibancoMethod && (
                    <PaymentMethodCard
                        title={multibancoMethod.name || ''}
                        description={translations.multibancoDescription}
                        logo={multibancoMethod.image?.src}
                        radioButton={{
                            value: multibancoMethod.uuid || '',
                            checked: selectedMethod === multibancoMethod.uuid,
                            onChange: () => setSelectedMethod(multibancoMethod.uuid || ''),
                        }}
                    />
                )}

                {/* mbway */}
                {mbwayMethod && (
                    <PaymentMethodCard
                        title={mbwayMethod.name || ''}
                        description={translations.mbwayDescription}
                        logo={mbwayMethod.image?.src}
                        radioButton={{
                            value: mbwayMethod.uuid || '',
                            checked: selectedMethod === mbwayMethod.uuid,
                            onChange: () => setSelectedMethod(mbwayMethod.uuid || ''),
                        }}>
                        <div className="mt-4">
                            <Input
                                label={translations.mbwayPhoneNumber}
                                value={mbwayPhoneNumber}
                                onChange={(value) => setMbwayPhoneNumber(formatPtPhoneNumberInput(value))}
                                type={InputTypes.tel}
                                variant={InputVariants.labeledInput}
                                state={errors.phoneNumber.length > 0 ? InputStates.error : undefined}
                                errorMessages={errors.phoneNumber}
                            />
                        </div>
                    </PaymentMethodCard>
                )}

                {/* debit or credit card */}
                {cardMethod && (
                    <PaymentMethodCard
                        title={cardMethod.name || ''}
                        logo={cardMethod.image?.src}
                        radioButton={{
                            value: cardMethod.uuid || '',
                            checked: selectedMethod === cardMethod.uuid,
                            onChange: () => setSelectedMethod(cardMethod.uuid || ''),
                        }}
                    />
                )}
            </>
        </ServicesCartContainer>
    );
};
